import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Plus, Pencil, Trash2, ChevronDown, ChevronUp, X, Eye, EyeOff } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/lib/supabase";

export function UserManagement() {
  const [users, setUsers] = useState([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    name: "",
    role: "vendedor",
    cod_rep: "",
    comision: 0,
    ver_comisiones_globales: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los usuarios",
        variant: "destructive"
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (editingUserId) {
        const { error } = await supabase
          .from('users')
          .update({
            username: formData.username,
            password: formData.password,
            name: formData.name,
            role: formData.role,
            cod_rep: formData.cod_rep,
            comision: formData.comision,
            ver_comisiones_globales: formData.ver_comisiones_globales
          })
          .eq('id', editingUserId);

        if (error) throw error;

        toast({
          title: "Usuario actualizado",
          description: "El usuario se ha actualizado exitosamente",
        });
        setEditingUserId(null);
      } else {
        // Llamar a la función Edge para crear el usuario
        console.log('Enviando datos para crear usuario:', { ...formData, password: '[HIDDEN]' });
        console.log('URL de la función Edge:', `${supabase.supabaseUrl}/functions/v1/create-user`);
        
        try {
          const response = await fetch(`${supabase.supabaseUrl}/functions/v1/create-user`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabase.supabaseKey}`
            },
            body: JSON.stringify(formData)
          });

          console.log('Status de la respuesta:', response.status);
          console.log('Headers de la respuesta:', Object.fromEntries(response.headers.entries()));

          const data = await response.json();
          console.log('Respuesta de la función Edge:', data);

          if (!response.ok) {
            // Manejar específicamente el error de clave duplicada
            if (data.error && data.error.includes('duplicate key value')) {
              toast({
                title: "Error",
                description: "El usuario ya existe en el sistema. Intenta con un nombre de usuario diferente.",
                variant: "destructive"
              });
            } else {
              toast({
                title: "Error",
                description: data.error || "No se pudo crear el usuario",
                variant: "destructive"
              });
            }
            return;
          }

          toast({
            title: "Usuario creado",
            description: "El usuario se ha creado exitosamente",
          });
          setShowNewForm(false);
        } catch (fetchError) {
          console.error('Error en la llamada a la función Edge:', fetchError);
          toast({
            title: "Error",
            description: "No se pudo conectar con el servidor para crear el usuario",
            variant: "destructive"
          });
          return;
        }
      }

      await fetchUsers();
      resetForm();
    } catch (error) {
      console.error('Error saving user:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo guardar el usuario",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      username: "",
      password: "",
      name: "",
      role: "vendedor",
      cod_rep: "",
      comision: 0,
      ver_comisiones_globales: false
    });
    setShowPassword(false);
  };

  const handleEdit = (user) => {
    setEditingUserId(user.id);
    setFormData({
      username: user.username,
      password: user.password,
      name: user.name,
      role: user.role,
      cod_rep: user.cod_rep || "",
      comision: user.comision || 0,
      ver_comisiones_globales: user.ver_comisiones_globales || false
    });
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    resetForm();
  };

  const handleDelete = async (userId) => {
    try {
      if (userId === "admin") {
        toast({
          title: "Error",
          description: "No se puede eliminar el usuario administrador",
          variant: "destructive"
        });
        return;
      }

      // Buscar el usuario para mostrar su nombre en la confirmación
      const userToDelete = users.find(user => user.id === userId);
      if (!userToDelete) return;

      // Mostrar confirmación antes de eliminar
      const confirmed = window.confirm(
        `¿Estás seguro de que quieres eliminar al usuario "${userToDelete.name}"?\n\nEsta acción eliminará el usuario del sistema y no se puede deshacer.`
      );

      if (!confirmed) return;

      // Eliminar de la tabla users
      const { error: userError } = await supabase
        .from('users')
        .delete()
        .eq('id', userId);

      if (userError) throw userError;

      await fetchUsers();
      toast({
        title: "Usuario eliminado",
        description: `El usuario "${userToDelete.name}" se ha eliminado exitosamente`,
        variant: "destructive"
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el usuario",
        variant: "destructive"
      });
    }
  };

  const renderUserForm = (isNewUser = false) => (
    <motion.form
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      onSubmit={handleSubmit}
      className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-4"
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="block text-sm font-medium mb-1">Usuario</label>
          <Input
            required
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Contraseña</label>
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="pr-10"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-gray-500" />
              ) : (
                <Eye className="h-4 w-4 text-gray-500" />
              )}
            </Button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Nombre</label>
          <Input
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Rol</label>
          <select
            className="w-full rounded-md border border-input bg-background px-3 py-2"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          >
            <option value="vendedor">Vendedor</option>
            <option value="cliente">Cliente</option>
            <option value="admin">Administrador</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Código de Representante</label>
          <Input
            value={formData.cod_rep}
            onChange={(e) => setFormData({ ...formData, cod_rep: e.target.value })}
            placeholder="Código del representante"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Comisión (%)</label>
          <Input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={formData.comision}
            onChange={(e) => setFormData({ ...formData, comision: parseFloat(e.target.value) || 0 })}
            placeholder="Porcentaje de comisión"
          />
        </div>
        <div className="md:col-span-2">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="ver_comisiones_globales"
              checked={formData.ver_comisiones_globales}
              onChange={(e) => setFormData({ ...formData, ver_comisiones_globales: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300"
            />
            <label htmlFor="ver_comisiones_globales" className="text-sm font-medium">
              Ver comisiones globales
            </label>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button 
          type="button" 
          variant="outline" 
          onClick={isNewUser ? () => setShowNewForm(false) : handleCancelEdit}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {editingUserId ? "Actualizar" : isSubmitting ? "Guardando..." : "Crear"}
        </Button>
      </div>
    </motion.form>
  );

  return (
    <div className="space-y-8">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Gestión de Usuarios</h2>
          <Button 
            onClick={() => {
              setShowNewForm(!showNewForm);
              setEditingUserId(null);
              resetForm();
            }}
            variant={showNewForm ? "outline" : "default"}
          >
            <Plus className="mr-2 h-4 w-4" /> 
            {showNewForm ? "Cancelar" : "Nuevo Usuario"}
          </Button>
        </div>

        <AnimatePresence>
          {showNewForm && renderUserForm(true)}
        </AnimatePresence>

        <div className="grid gap-4">
          {users.map((user) => (
            <motion.div
              key={user.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-4 rounded-lg shadow-sm border border-gray-200"
            >
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <h3 className="font-semibold">{user.name}</h3>
                  <p className="text-sm text-gray-600">Usuario: {user.username}</p>
                  <p className="text-sm text-gray-600">Rol: {user.role}</p>
                  {user.cod_rep && (
                    <p className="text-sm text-gray-600">Código: {user.cod_rep}</p>
                  )}
                  <p className="text-sm text-gray-600">Comisión: {user.comision}%</p>
                  <p className="text-sm text-gray-600">
                    Ver comisiones globales: {user.ver_comisiones_globales ? "Sí" : "No"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleEdit(user)}
                    disabled={user.id === "admin"}
                  >
                    {editingUserId === user.id ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => handleDelete(user.id)}
                    disabled={user.id === "admin"}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <AnimatePresence>
                {editingUserId === user.id && renderUserForm(false)}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
